import { Dispatch } from 'redux';
import { WsCommandSubject } from '../crud/types';
import { prepareCreateCommandAction, prepareReadCommandAction, prepareUpdateCommandAction, prepareDeleteCommandAction } from '../crud/actions';
import { RecordAccessLevel } from 'data-types';
import type { SearchType } from 'ws-messages';
import { IAppState } from '../store/types';
import {
  IRecord,
  IRecordConfirmationData,
  SET_INITIAL_LOAD_IN_PROGRESS,
  ISetInitialLoadInProgressAction,
  RESET_RECORDS,
  ADD_RECORD,
  APPEND_RECORDS,
  SET_CURRENT_RECORD,
  SET_RECORD_CONFIRMED_BY_BACKEND,
  SET_RECORD_STATE,
  DELETE_RECORD,
  UNLINK_RECORD_TENANT,
  ViewMode,
  SET_VIEW_MODE,
  TOGGLE_EXPAND_CONDENSED_VIEW_MODE,
  SET_SHOW_SUMMARIES,
  SET_SHOW_ALL_LINKABLE,
  SET_SEARCH_TEXT,
  SET_SEARCH_TYPE,
  SET_INCLUDE_ARCHIVED,
  SET_LATEST_PUBLIC_RECORDS_WITH_SUMMARIES,
  ADD_LATEST_PUBLIC_RECORDS,
  UPDATE_LATEST_PUBLIC_RECORD,
  REMOVE_LATEST_PUBLIC_RECORD,
  SET_SELECTED_SUMMARIES_RECORD_ID,
  SET_RECORD_AS_NON_LINKABLE,
  TOGGLE_RECORD_MAXIMIZE,
  UPDATE_RECORD_FIELD,
  SET_RECORD_FORM_ERRORS,
  RESET_RECORD_FORM,
  ICreateRecordParams,
  IAddRecordAction,
  IAppendRecordsAction,
  ISetCurrentRecordAction,
  ISetRecordConfirmedByBackendAction,
  ISetRecordStateAction,
  IDeleteRecordAction,
  IUnlinkRecordTenantAction,
  ISetViewModeAction,
  IToggleExpandCondensedViewModeAction,
  ISetShowSummariesAction,
  ISetShowAllLinkableAction,
  ISetSearchTextAction,
  ISetSearchTypeAction,
  ISetIncludeArchivedAction,
  IUpdateRecordFieldAction,
  ISetRecordFormErrorsAction,
  IResetRecordFormAction,
  ISetRecordAsNonLinkableAction,
  IToggleRecordMaximizeAction,
  ILinkRecordParams,
  IResetRecordsAction,
  ILatestPublicRecord,
  ISetLatestPublicRecordsAction,
  IAddLatestPublicRecordsAction,
  IUpdateLatestPublicRecordAction,
  IRemoveLatestPublicRecordAction,
  ISetSelectedSummariesRecordIdAction,
} from './types';

// Record-specific action creators
export const prepareCreateRecordCommandAction = (
  record_id: string,
  user_text: string,
  ai_text: string | undefined,
  llm_profile_id: string,
  access_level?: RecordAccessLevel,
  shareWithSaasTenantIds?: string[],
) =>
  prepareCreateCommandAction({
    type: 'records',
    params: {
      record_id,
      llm_profile_id,
      user_text,
      ai_text,
      access_level,
      shareWithSaasTenantIds,
    } as ICreateRecordParams,
  });

export const prepareLinkRecordCommandAction = (record_id: string, saas_tenant_id: string) =>
  prepareCreateCommandAction({
    type: 'records',
    params: { record_id, saas_tenant_id } as ILinkRecordParams,
  });

// saas_tenant_id: the saas tenant that owns the record
export const prepareUnlinkRecordCommandAction = ({ record_id, saas_tenant_id }: { record_id: string; saas_tenant_id: string }) =>
  prepareDeleteCommandAction({
    type: 'records',
    params: { record_id, saas_tenant_id } as ILinkRecordParams,
  });

export const setInitialLoadInProgressAction = (inProgress: boolean): ISetInitialLoadInProgressAction => ({
  type: SET_INITIAL_LOAD_IN_PROGRESS,
  payload: inProgress,
});

export const resetRecordsAction = (): IResetRecordsAction => ({
  type: RESET_RECORDS,
});

export const addRecordAction = (record: IRecord): IAddRecordAction => ({
  type: ADD_RECORD,
  payload: record,
});

export const appendRecordsAction = (records: IRecord[]): IAppendRecordsAction => ({
  type: APPEND_RECORDS,
  payload: records,
});

export const setSearchTextAction = (searchText: string): ISetSearchTextAction => ({
  type: SET_SEARCH_TEXT,
  payload: searchText,
});

export const setSearchTypeAction = (searchType?: SearchType): ISetSearchTypeAction => ({
  type: SET_SEARCH_TYPE,
  payload: searchType,
});

export const setIncludeArchivedAction = (includeArchived: boolean): ISetIncludeArchivedAction => ({
  type: SET_INCLUDE_ARCHIVED,
  payload: includeArchived,
});

export const setCurrentRecordAction = (record_id: string) => (dispatch: Dispatch) => {
  const action: ISetCurrentRecordAction = {
    type: SET_CURRENT_RECORD,
    payload: record_id,
  };
  dispatch(action);
};

export const prepareReadRecordsCommandAction = (params?: {
  latestPublic?: boolean;
  record_id?: string;
  searchText?: string;
  searchType?: SearchType;
  readAllLinkable?: boolean;
  includeArchived?: boolean;
}) =>
  prepareReadCommandAction({
    type: 'records' as WsCommandSubject,
    params: params || {},
  });

export const setRecordConfirmedByBackendAction = (record_id: string, data: IRecordConfirmationData): ISetRecordConfirmedByBackendAction => ({
  type: SET_RECORD_CONFIRMED_BY_BACKEND,
  record_id,
  data,
});

export const prepareUpdateRecordCommandAction = (record_id: string, updates: Partial<IRecord>) =>
  prepareUpdateCommandAction({
    type: 'records' as WsCommandSubject,
    params: {
      record_id: record_id,
      ...updates,
    },
  });

export const setRecordStateAction = (update: Partial<IRecord> & { record_id: string }): ISetRecordStateAction => ({
  type: SET_RECORD_STATE,
  payload: update,
});

export const prepareDeleteRecordCommandAction = (record_id: string, record_saas_tenant_id: string) =>
  prepareDeleteCommandAction({
    type: 'records' as WsCommandSubject,
    params: { record_id, saas_tenant_id: record_saas_tenant_id },
  });

export const deleteRecordAction = (record_id: string): IDeleteRecordAction => ({
  type: DELETE_RECORD,
  payload: record_id,
});

/**
 * Deletes a record from the store and reads the next current record if it changed.
 * @param record_id - The ID of the record to delete
 */
export const deleteRecordAndReadNextAction = (record_id: string) => (dispatch: Dispatch, getState: () => IAppState) => {
  const prevCurrentRecordId = getState().records.currentRecordId;
  dispatch(deleteRecordAction(record_id));
  const nextCurrentRecordId = getState().records.currentRecordId;
  if (nextCurrentRecordId && nextCurrentRecordId !== prevCurrentRecordId) {
    dispatch(prepareReadRecordsCommandAction({ record_id: nextCurrentRecordId }));
  }
};

export const unlinkRecordTenantAction = (record_id: string, unlinkedBy: string): IUnlinkRecordTenantAction => ({
  type: UNLINK_RECORD_TENANT,
  payload: { record_id, unlinkedBy },
});

// View mode management
export const setViewModeAction = (viewMode: ViewMode): ISetViewModeAction => ({
  type: SET_VIEW_MODE,
  payload: viewMode,
});

export const toggleExpandCondensedViewModeAction = (): IToggleExpandCondensedViewModeAction => ({
  type: TOGGLE_EXPAND_CONDENSED_VIEW_MODE,
});

export const setShowSummariesAction = (showSummaries: boolean): ISetShowSummariesAction => ({
  type: SET_SHOW_SUMMARIES,
  payload: showSummaries,
});

export const setShowAllLinkableAction = (showAllLinkable: boolean): ISetShowAllLinkableAction => ({
  type: SET_SHOW_ALL_LINKABLE,
  payload: showAllLinkable,
});

// Latest records management
export const setLatestPublicRecordsAction = (records: ILatestPublicRecord[]): ISetLatestPublicRecordsAction => ({
  type: SET_LATEST_PUBLIC_RECORDS_WITH_SUMMARIES,
  payload: records,
});

export const addLatestPublicRecordsAction = (record: ILatestPublicRecord): IAddLatestPublicRecordsAction => ({
  type: ADD_LATEST_PUBLIC_RECORDS,
  payload: record,
});

export const updateLatestPublicRecordAction = (record_id: string): IUpdateLatestPublicRecordAction => ({
  type: UPDATE_LATEST_PUBLIC_RECORD,
  payload: record_id,
});

export const removeLatestPublicRecordAction = (record_id: string): IRemoveLatestPublicRecordAction => ({
  type: REMOVE_LATEST_PUBLIC_RECORD,
  payload: record_id,
});

export const setSelectedSummariesRecordIdAction = (recordId: string | null): ISetSelectedSummariesRecordIdAction => ({
  type: SET_SELECTED_SUMMARIES_RECORD_ID,
  payload: recordId,
});

export const setRecordAsNonLinkableAction = (record_id: string): ISetRecordAsNonLinkableAction => ({
  type: SET_RECORD_AS_NON_LINKABLE,
  payload: record_id,
});

export const toggleRecordMaximizeAction = (recordOrSubrecordId: string): IToggleRecordMaximizeAction => ({
  type: TOGGLE_RECORD_MAXIMIZE,
  payload: recordOrSubrecordId,
});

// Record form
export const updateRecordFieldAction = (field: string, value: string | number | boolean | string[]): IUpdateRecordFieldAction => ({
  type: UPDATE_RECORD_FIELD,
  payload: { field, value },
});

export const setRecordFormErrorsAction = (errors: Record<string, string>): ISetRecordFormErrorsAction => ({
  type: SET_RECORD_FORM_ERRORS,
  payload: errors,
});

export const resetRecordFormAction = (): IResetRecordFormAction => ({
  type: RESET_RECORD_FORM,
});
